import {
    useReactTable,
    getCoreRowModel,
    getSortedRowModel,
    getPaginationRowModel,
    flexRender,
    SortingState,
} from '@tanstack/react-table';
import { useMemo, useState, useEffect, useCallback } from 'react';
import tableIcon from '../../assets/images/common/table_icon.png';
import copyIcon from '../../assets/images/common/copy.svg';
import checkIcon from '../../assets/images/common/check.svg';
import csvIcon from '../../assets/images/common/file-csv.svg';
import excelIcon from '../../assets/images/common/file-excel.svg';
import {
    copyTableToClipboard,
    exportTableToCSV,
    exportTableToExcel,
    getRatioColumns,
    getTransitionColumns,
    getDefaultSorting,
    TimeFormat,
    RatioColumnLabels,
    TransitionColumnLabels,
} from './util/Table';
import Select from '../../components/Select';
import { useTranslation } from 'react-i18next';
import { useUser } from '../../hooks/UserContext';
import { LockIcon } from '../../components/icons/LockIcon';
import { ExportLockedModal } from '../../components/ExportLockedModal';
import { openPlansWithTracking } from '../../utils/openPlansWithTracking';
import { getZonedDate } from '../../utils/dayjs';

// Propsの型定義
type Props = {
    value: any[];
    tableType: 'ratio' | 'transition';
};

/**
 * テーブルコンポーネント
 * @param tableType テーブルの種類
 * @param value テーブルのデータ
 * @returns テーブル
 */
export const Table = ({ tableType, value }: Props) => {
    const [sorting, setSorting] = useState<SortingState>(getDefaultSorting(tableType));
    const [timeFormat, setTimeFormat] = useState<TimeFormat>('formatted');
    const [copied, setCopied] = useState(false);
    const [pageSize, setPageSize] = useState<number>(10);
    const [isExportLockedModalOpen, setIsExportLockedModalOpen] = useState(false);
    const [exportType, setExportType] = useState<'csv' | 'excel' | null>(null);
    const { t } = useTranslation('analysis');
    const { t: tCommon } = useTranslation('common');
    const { userInfo } = useUser();
    const isPaid = userInfo?.isPaid ?? false;
    const dataLength = value.length;

    const ratioColumnLabels: RatioColumnLabels = useMemo(
        () => ({
            title: t('table.columns.title'),
            percentage: t('table.columns.percentage'),
            totalTime: t('table.columns.totalTime'),
            count: t('table.columns.count'),
            averageTimePerCount: t('table.columns.avgPerCount'),
            averageTimePerDay: t('table.columns.avgPerDay'),
        }),
        [t],
    );

    const transitionColumnLabels: TransitionColumnLabels = useMemo(
        () => ({
            date: t('table.columns.date'),
            time: t('table.columns.time'),
        }),
        [t],
    );

    const formatTime = useCallback(
        (minutes: number, format: TimeFormat) => {
            const hours = Math.floor(minutes / 60);
            const mins = minutes % 60;

            if (format === 'minutes') {
                return `${minutes}`;
            }

            if (format === 'clock') {
                const paddedHours = hours.toString().padStart(2, '0');
                const paddedMinutes = mins.toString().padStart(2, '0');
                return `${paddedHours}:${paddedMinutes}`;
            }

            const parts: string[] = [];
            if (hours > 0) {
                parts.push(tCommon('time.hoursShort', { count: hours }));
            }
            if (mins > 0 || parts.length === 0) {
                parts.push(tCommon('time.minutesShort', { count: mins }));
            }
            // join without space to mimic compact notation like 1h20m / 1時間20分
            return parts.join('');
        },
        [tCommon],
    );

    // 時間表示形式のオプション
    const timeFormatOptions = useMemo(
        () => [
            { value: 'minutes', label: t('table.timeFormats.minutes') },
            { value: 'formatted', label: t('table.timeFormats.formatted') },
            { value: 'clock', label: t('table.timeFormats.clock') },
        ],
        [t],
    );

    const columns = useMemo(() => {
        return tableType === 'ratio'
            ? getRatioColumns(timeFormat, formatTime, ratioColumnLabels)
            : getTransitionColumns(timeFormat, formatTime, transitionColumnLabels);
    }, [tableType, timeFormat, formatTime, ratioColumnLabels, transitionColumnLabels]);

    // 表示件数のオプション
    const pageSizeOptions = useMemo(
        () => [
            { value: '10', label: t('table.pageSizeOption', { count: 10 }) },
            { value: '25', label: t('table.pageSizeOption', { count: 25 }) },
            { value: dataLength.toString(), label: t('table.pageSizeAll') },
        ],
        [t, dataLength],
    );

    const table = useReactTable({
        data: value,
        columns,
        state: {
            sorting,
        },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        initialState: {
            pagination: { pageSize: pageSize },
        },
        manualPagination: false,
    });

    useEffect(() => {
        table.setPageSize(pageSize);
        table.setPageIndex(0);
    }, [pageSize, table]);

    const showPagination = dataLength > 10;

    return (
        <>
            {/* 値が存在しない場合 */}
            {value.length === 0 && (
                <div className="w-full h-full flex flex-col justify-center items-center">
                    <img src={tableIcon} alt="table" className="w-16 h-16 image-protection" />
                    <p className="mt-4 text-center">{t('table.noData')}</p>
                </div>
            )}
            {/* 値が存在する場合 */}
            {value.length > 0 && (
                <div className="w-full p-4 lg:p-6 h-full flex flex-col justify-start overflow-auto">
                    {/* オプション */}
                    <div className="flex flex-wrap justify-start items-start lg:items-center mb-6 gap-6">
                        {/* 表示件数と時間表示形式のグループ */}
                        <div className="flex flex-wrap items-center gap-6">
                        {/* 表示件数 */}
                        <div className="flex items-center">
                            <label className="text-sm font-medium mr-2">
                                {t('table.pageSizeLabel')}
                            </label>
                            <Select
                                options={pageSizeOptions}
                                defaultValue={
                                    pageSizeOptions.find(
                                        (opt) => opt.value === pageSize.toString(),
                                    ) || pageSizeOptions[0]
                                }
                                onValueChange={(option) => setPageSize(Number(option.value))}
                                className="text-base w-auto min-w-[100px]"
                            />
                        </div>
                        {/* 時間表示形式 */}
                        <div className="flex items-center">
                            <label className="text-sm font-medium mr-2">
                                {t('table.timeFormatLabel')}
                            </label>
                            <Select
                                options={timeFormatOptions}
                                defaultValue={
                                    timeFormatOptions.find((opt) => opt.value === timeFormat) ||
                                    timeFormatOptions[0]
                                }
                                onValueChange={(option) =>
                                    setTimeFormat(option.value as TimeFormat)
                                }
                                className="text-base w-auto min-w-[150px]"
                            />
                        </div>
                        </div>
                    </div>

                    {/* テーブル */}
                    <div className="border-1 border-gray-300 rounded-md overflow-auto max-h-full">
                        <table className="table-auto w-full">
                            <thead className="bg-gray-100 sticky top-0 z-10">
                                {table.getHeaderGroups().map((headerGroup) => (
                                    <tr key={headerGroup.id}>
                                        {headerGroup.headers.map((header) => (
                                            <th
                                                key={header.id}
                                                onClick={header.column.getToggleSortingHandler()}
                                                className="cursor-pointer p-2 border text-left select-none bg-gray-100 font-normal"
                                            >
                                                {flexRender(
                                                    header.column.columnDef.header,
                                                    header.getContext(),
                                                )}{' '}
                                                <span>
                                                    {header.column.getIsSorted() === 'asc' && '↑'}
                                                    {header.column.getIsSorted() === 'desc' && '↓'}
                                                    {!header.column.getIsSorted() && '↕'}
                                                </span>
                                            </th>
                                        ))}
                                    </tr>
                                ))}
                            </thead>
                            <tbody>
                                {table.getRowModel().rows.map((row, rowIndex) => (
                                    <tr
                                        key={row.id}
                                        className={rowIndex % 2 === 1 ? 'bg-gray-50' : ''}
                                    >
                                        {row.getVisibleCells().map((cell) => (
                                            <td key={cell.id} className="p-2 border">
                                                {flexRender(
                                                    cell.column.columnDef.cell,
                                                    cell.getContext(),
                                                )}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* ページネーションと全件数 */}
                    <div className="flex items-center mt-4">
                        {/* 全件数表示（左側） */}
                        <div className="flex items-center flex-1">
                            <span className="text-sm text-gray-600">
                                {t('table.totalCount', { count: dataLength })}
                            </span>
                        </div>
                        {/* ページネーション（中央） */}
                    {showPagination && (
                            <div className="flex items-center gap-4 flex-1 justify-center">
                            <button
                                onClick={() => table.previousPage()}
                                disabled={!table.getCanPreviousPage()}
                                className="px-2 py-1 border rounded disabled:opacity-50"
                            >
                                {t('table.pagination.prev')}
                            </button>
                            <span className="text-sm">
                                {t('table.pageIndicator', {
                                    current: table.getState().pagination.pageIndex + 1,
                                    total: table.getPageCount(),
                                })}
                            </span>
                            <button
                                onClick={() => table.nextPage()}
                                disabled={!table.getCanNextPage()}
                                className="px-2 py-1 border rounded disabled:opacity-50"
                            >
                                {t('table.pagination.next')}
                            </button>
                        </div>
                    )}
                        {/* 右側のスペーサー（バランス用） */}
                        <div className="flex-1"></div>
                    </div>

                    {/* ボタン群（表の下、中央寄せ） */}
                    <div className="flex justify-center items-center gap-2 mt-6">
                        {/* 表をコピー */}
                        <button
                            onClick={() =>
                                copyTableToClipboard(table, timeFormat, formatTime, setCopied)
                            }
                            className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-100 flex items-center gap-2 transition-colors bg-white shadow-sm"
                            title={t('table.copy.copy')}
                        >
                            <img
                                src={copied ? checkIcon : copyIcon}
                                alt={copied ? 'copied' : 'copy'}
                                className="w-4 h-4"
                            />
                            <span>{copied ? t('table.copy.copied') : t('table.copy.copy')}</span>
                        </button>
                        {/* CSVエクスポート（モバイルでは非表示） */}
                        <button
                            onClick={() => {
                                if (!isPaid) {
                                    setExportType('csv');
                                    setIsExportLockedModalOpen(true);
                                    return;
                                }
                                const zonedNow = getZonedDate(userInfo?.timeZone);
                                const dateStr = zonedNow.format('YYYY-MM-DD');
                                const timeStr = zonedNow.format('HHmm');
                                exportTableToCSV(
                                    value,
                                    columns,
                                    timeFormat,
                                    formatTime,
                                    `kotomil-analysis-${tableType}-${dateStr}-${timeStr}.csv`,
                                );
                            }}
                            className="hidden lg:flex px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-100 items-center gap-2 transition-colors bg-white shadow-sm relative"
                            title={isPaid ? t('table.export.csv') : t('table.export.locked.tooltip')}
                        >
                            <img
                                src={csvIcon}
                                alt="CSV export"
                                className="w-4 h-4"
                            />
                            <span>{t('table.export.csv')}</span>
                            {!isPaid && (
                                <span className="absolute -top-1 -right-1 flex items-center justify-center w-5 h-5 bg-white rounded-full border border-gray-300 shadow-sm">
                                    <LockIcon className="w-4 h-4 text-gray-500" />
                                </span>
                            )}
                        </button>
                        {/* Excelエクスポート（モバイルでは非表示） */}
                        <button
                            onClick={() => {
                                if (!isPaid) {
                                    setExportType('excel');
                                    setIsExportLockedModalOpen(true);
                                    return;
                                }
                                const zonedNow = getZonedDate(userInfo?.timeZone);
                                const dateStr = zonedNow.format('YYYY-MM-DD');
                                const timeStr = zonedNow.format('HHmm');
                                exportTableToExcel(
                                    value,
                                    columns,
                                    timeFormat,
                                    formatTime,
                                    `kotomil-analysis-${tableType}-${dateStr}-${timeStr}.xlsx`,
                                );
                            }}
                            className="hidden lg:flex px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-100 items-center gap-2 transition-colors bg-white shadow-sm relative"
                            title={isPaid ? t('table.export.excel') : t('table.export.locked.tooltip')}
                        >
                            <img
                                src={excelIcon}
                                alt="Excel export"
                                className="w-4 h-4"
                            />
                            <span>{t('table.export.excel')}</span>
                            {!isPaid && (
                                <span className="absolute -top-1 -right-1 flex items-center justify-center w-5 h-5 bg-white rounded-full border border-gray-300 shadow-sm">
                                    <LockIcon className="w-4 h-4 text-gray-500" />
                                </span>
                            )}
                        </button>
                    </div>
                </div>
            )}
            {/* エクスポート機能ロックモーダル */}
            <ExportLockedModal
                isOpen={isExportLockedModalOpen}
                onClose={() => {
                    setIsExportLockedModalOpen(false);
                    setExportType(null);
                }}
                onGoPlans={() => {
                    setIsExportLockedModalOpen(false);
                    const exportTypeStr = exportType || 'export';
                    openPlansWithTracking({
                        source: `export_modal_${exportTypeStr}`,
                        panelId: 'anTable',
                    });
                    setExportType(null);
                }}
            />
        </>
    );
};

export default Table;
